import { API_BASE_URL } from './api';

/**
 * Admin authentication API.
 *
 * Security design:
 * - All auth state is managed server-side via httpOnly cookies
 * - No tokens, JWTs, or secrets are ever stored or accessible in the browser
 * - Cookies are set by the backend with httpOnly + Secure + SameSite flags
 * - Frontend only knows if the user "is authenticated" via a verify endpoint
 */

interface AuthResponse {
  success: boolean;
  message?: string;
}

export const authApi = {
  /**
   * Login with admin credentials.
   * Backend sets an httpOnly session cookie on success.
   * No token is returned to the frontend.
   */
  login: async (username: string, password: string): Promise<AuthResponse> => {
    const response = await fetch(`${API_BASE_URL}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Send/receive httpOnly cookies
      body: JSON.stringify({ username, password }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Invalid credentials');
    }

    return data;
  },

  /**
   * Logout the current admin session.
   * Backend clears the httpOnly session cookie.
   */
  logout: async (): Promise<void> => {
    await fetch(`${API_BASE_URL}/api/admin/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  },

  /**
   * Verify the current session is still valid.
   * Checks the httpOnly cookie server-side.
   * Returns true if authenticated, false otherwise.
   */
  verify: async (): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/verify`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) return false;

      const data = await response.json();
      return data.success === true;
    } catch {
      return false;
    }
  },
};
